import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, BookOpen } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { RunbookDetail } from "../../pages/Runbooks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Opens a single client's runbook in a modal (instead of the full-screen page).
// Reuses the exact RunbookDetail view + reveal/edit/save/mark-reviewed behaviour.
export function RunbookModal({ open, onOpenChange, company, clientId, startInEdit = false, agentTheme = "light" }) {
  const [selected, setSelected] = useState(null);
  const [infra, setInfra] = useState({ db: null, wcf: [], loading: false });
  const [revealed, setRevealed] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const actor = (() => {
    try { return (JSON.parse(localStorage.getItem("equipr_session") || "{}").id) || ""; }
    catch { return ""; }
  })();

  const load = useCallback(async (withReveal = false) => {
    setLoading(true);
    try {
      // Resolve the company NAME to a real client_id (mirrors the full page's
      // forgiving match) so we open an EXISTING runbook rather than a blank slug.
      let cid = clientId || "";
      if (!cid && company) {
        const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
        const lcp = (a, b) => { let i = 0; while (i < a.length && i < b.length && a[i] === b[i]) i++; return i; };
        const q = norm(company);
        try {
          const list = (await axios.get(`${API}/runbooks/partners`)).data || [];
          let target = list.find((r) => norm(r.company_name) === q)
            || list.find((r) => { const n = norm(r.company_name); return n && (n.includes(q) || q.includes(n)); });
          if (!target) {
            let best = null, bestScore = 0;
            for (const r of list) { const s = lcp(norm(r.company_name), q); if (s > bestScore) { best = r; bestScore = s; } }
            if (bestScore >= 5) target = best;
          }
          if (target) cid = target.client_id || (target.equipr || {}).client_id || "";
        } catch { /* fall back to slug below */ }
      }
      if (!cid) cid = (company || "").replace(/[^A-Za-z0-9]/g, "_").toUpperCase().slice(0, 24);
      let doc = null;
      try {
        const res = await axios.get(`${API}/runbooks/${cid}`, {
          params: withReveal ? { reveal: true } : {},
          headers: { "X-Actor": actor },
        });
        doc = res.data;
      } catch (err) {
        if (err?.response?.status === 404) doc = { client_id: cid, company_name: company || cid };
        else throw err;
      }
      setSelected(doc);
      setRevealed(withReveal);
      setDraft(null);
      setEditMode(false);
      const key = doc.client_id || doc.company_name || company || "";
      if (key) {
        setInfra({ db: null, wcf: [], loading: true });
        const [dbRes, wcfRes] = await Promise.allSettled([
          axios.get(`${API}/databases/company/${encodeURIComponent(key)}`),
          axios.get(`${API}/wcf/company/${encodeURIComponent(key)}`),
        ]);
        const db = dbRes.status === "fulfilled" && dbRes.value.data?.found ? dbRes.value.data.health : null;
        const wcf = wcfRes.status === "fulfilled" && wcfRes.value.data?.found ? (wcfRes.value.data.services || []) : [];
        setInfra({ db, wcf, loading: false });
      }
    } catch (e) {
      toast.error("Failed to load runbook");
    } finally { setLoading(false); }
  }, [clientId, company, actor]);

  const editRequested = useRef(false);

  useEffect(() => {
    if (open && (company || clientId)) load(false);
    if (!open) {
      setSelected(null); setDraft(null); setEditMode(false); setRevealed(false);
      editRequested.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, company, clientId]);

  // "Edit" on the client record should land in edit mode rather than making the
  // user find the button again. Once per opening: cancelling edit must not bounce
  // straight back into it.
  useEffect(() => {
    if (open && startInEdit && selected && !editRequested.current) {
      editRequested.current = true;
      startEdit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, startInEdit, selected]);

  const toggleReveal = async () => {
    await load(!revealed);
    if (!revealed) toast.success("Secrets revealed — recorded in audit log");
  };
  const startEdit = () => {
    if (!selected) return;
    const d = JSON.parse(JSON.stringify(selected));
    if (d.allocate_login?.password === "••••••••") d.allocate_login.password = "";
    if (d.sms_provider?.password === "••••••••") d.sms_provider.password = "";
    setDraft(d); setEditMode(true);
  };
  const saveDraft = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const payload = JSON.parse(JSON.stringify(draft));
      if (payload.allocate_login && !payload.allocate_login.password) delete payload.allocate_login.password;
      if (payload.sms_provider && !payload.sms_provider.password) delete payload.sms_provider.password;
      await axios.put(`${API}/runbooks/${draft.client_id}`, payload, { headers: { "X-Actor": actor } });
      toast.success("Runbook saved");
      setEditMode(false);
      await load(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save runbook");
    } finally { setSaving(false); }
  };
  const markReviewed = async () => {
    if (!selected) return;
    try {
      await axios.post(`${API}/runbooks/${selected.client_id}/mark-reviewed`, null, { headers: { "X-Actor": actor } });
      toast.success("Marked as reviewed");
      await load(revealed);
    } catch (e) { toast.error("Failed to mark reviewed"); }
  };
  const updateDraft = (path, value) => {
    setDraft((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) { cur[parts[i]] = cur[parts[i]] || {}; cur = cur[parts[i]]; }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-4xl w-[92vw] max-h-[88vh] overflow-y-auto"
        data-agent-theme={agentTheme}
        data-testid="runbook-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <BookOpen className="w-5 h-5 text-[#8A6D00]" /> Client Runbook
          </DialogTitle>
        </DialogHeader>
        {loading && !selected ? (
          <div className="py-20 flex items-center justify-center text-[#8A857A]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading runbook…
          </div>
        ) : (
          <RunbookDetail
            record={editMode ? draft : selected}
            editMode={editMode}
            revealed={revealed}
            saving={saving}
            infra={infra}
            onToggleReveal={toggleReveal}
            onStartEdit={startEdit}
            onCancelEdit={() => { setEditMode(false); setDraft(null); }}
            onSave={saveDraft}
            onMarkReviewed={markReviewed}
            onChange={updateDraft}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default RunbookModal;
